import { Card, CardContent, CardHeader, CardTitle, StatusChip, fmtRelativeTime, type Tone } from '@smena/ui';
import type { AccountCard } from './api';

// Только статусы. Расшифрованные bot_token_enc и ключи Avito сюда не приходят и не придут:
// админка отвечает на вопрос «работает ли подключение», а не «какой у клиента токен».

export function IntegrationsTab({ account }: { account: AccountCard }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Каналы</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {account.channels.length === 0 ? (
            <p className="text-muted">Каналы не настроены - гость не может написать.</p>
          ) : null}
          {account.channels.map((c) => (
            <div key={c.channel} className="flex items-start justify-between gap-3 border-b border-line/60 pb-2 last:border-0">
              <div>
                <div>
                  {c.channel}{' '}
                  <span className="text-xs text-muted">
                    {c.kind === 'shared' ? 'общий бот Smena' : 'свой бот УК'}
                  </span>
                </div>
                {c.botUsername ? (
                  <div className="font-mono text-[11px] text-muted">@{c.botUsername}</div>
                ) : null}
                {c.lastError ? (
                  <div className="text-[11px] text-destructive">{c.lastError}</div>
                ) : null}
              </div>
              <StatusChip tone={statusTone(c.status)}>{c.status}</StatusChip>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Avito</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {account.avito ? (
            <>
              <Row label="Режим авторизации" value={account.avito.authKind} mono />
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-muted">Статус</span>
                <StatusChip tone={statusTone(account.avito.status)}>{account.avito.status}</StatusChip>
              </div>
              <Row label="Тариф позволяет Messenger" value={account.avito.tariffOk ? 'да' : 'нет'} />
              <Row label="Последняя синхронизация" value={fmtRelativeTime(account.avito.lastSyncAt)} />
              {account.avito.lastError ? (
                <p className="text-xs text-destructive">{account.avito.lastError}</p>
              ) : null}
            </>
          ) : (
            <p className="text-muted">Не подключён.</p>
          )}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>iCal-фиды</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2 text-sm">
          {account.icalFeeds.length === 0 ? (
            <p className="text-muted">Фидов нет - брони не инжестятся автоматически (ADR-3).</p>
          ) : null}
          {account.icalFeeds.map((f) => (
            <div key={f.id} className="flex items-start justify-between gap-3 border-b border-line/60 pb-2 last:border-0">
              <div className="min-w-0">
                <div className="truncate">{f.label ?? 'без метки'}</div>
                <div className="text-[11px] text-muted">
                  прогон: {fmtRelativeTime(f.lastPolledAt)}
                  {f.lastEventsCount !== null ? ` · событий: ${f.lastEventsCount}` : ''}
                </div>
                {f.lastError ? (
                  <div className="text-[11px] text-destructive">{f.lastError}</div>
                ) : null}
              </div>
              <div className="flex shrink-0 gap-1">
                {!f.active ? <StatusChip tone="muted">выключен</StatusChip> : null}
                <StatusChip tone={statusTone(f.lastStatus ?? 'unknown')}>
                  {f.lastStatus ?? 'нет прогонов'}
                </StatusChip>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function statusTone(status: string): Tone {
  if (status === 'active' || status === 'ok') return 'green';
  if (status === 'error' || status === 'no_tariff') return 'red';
  if (status === 'inactive') return 'muted';
  return 'amber';
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 text-muted">{label}</span>
      <span className={mono ? 'truncate font-mono text-xs' : 'truncate'}>{value}</span>
    </div>
  );
}
